import { api } from 'api';
import { requestErrorAction } from 'common/utils/requestErrorHandling';
import { PaginatedResponse, PaginationParams } from '../types';
import {
  MollyWhitelabelCustomer,
  MollyWhitelabelCustomerStatuses,
  MollyWhitelabelCustomerSummary,
} from './actions';

// #region MollyWhitelabelCustomer
export const requestMollyWhitelabelCustomer = async (username: string) =>
  await api.get<MollyWhitelabelCustomer>(
    `/whitelabel/account/molly/${username}/`
  );

export const doGetMollyWhitelabelCustomer =
  (username: string) => async (dispatch) => {
    dispatch({ type: 'molly_whitelabel/customer/started' });

    try {
      const response = await requestMollyWhitelabelCustomer(username);

      dispatch({
        type: 'molly_whitelabel/customer/finished',
        customer: response.data,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'molly_whitelabel/customer/finished' },
          e,
          `Failed to molly whitelabel customer.`
        )
      );
    }
  };

export interface MollyWhitelabelCustomerSummariesParams
  extends PaginationParams {
  username?: string;
  status?: MollyWhitelabelCustomerStatuses;
  country?: string;
  kyc_submitted?: boolean;
}

export const requestMollyWhitelabelCustomerSummaries = async (
  params: MollyWhitelabelCustomerSummariesParams
) =>
  await api.get<PaginatedResponse<MollyWhitelabelCustomerSummary>>(
    `/whitelabel/account/molly/`,
    { params }
  );

export const doGetMollyWhitelabelCustomerSummaries =
  (params: MollyWhitelabelCustomerSummariesParams) => async (dispatch) => {
    dispatch({ type: 'molly_whitelabel/customers/started' });

    try {
      const response = await requestMollyWhitelabelCustomerSummaries(params);

      dispatch({
        type: 'molly_whitelabel/customers/finished',
        customers: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'molly_whitelabel/customers/finished' },
          e,
          `Failed to molly whitelabel customer summaries.`
        )
      );
    }
  };
// #endregion

// #region MollyWhitelabelCustomerKYCDocuments
export const requestMollyWhitelabelCustomerKYCDocuments = async (
  username: string
) =>
  await api.get<MollyWhitelabelCustomer>(
    `/whitelabel/document/molly/${username}/`
  );

export const doGetMollyWhitelabelCustomerKYCDocuments =
  (username: string) => async (dispatch) => {
    dispatch({ type: 'molly_whitelabel/customer/kyc/documents/started' });

    try {
      const response = await requestMollyWhitelabelCustomerKYCDocuments(
        username
      );

      dispatch({
        type: 'molly_whitelabel/customer/kyc/documents/finished',
        documents: response.data,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'molly_whitelabel/customer/kyc/documents/finished' },
          e,
          `Failed to molly whitelabel customer "${username}" kyc documents.`
        )
      );
    }
  };

export interface MollyWhitelabelKYCDocumentsParams extends PaginationParams {
  status?: string;
}

export const requestMollyWhitelabelKYCDocuments = async (
  params: MollyWhitelabelKYCDocumentsParams
) =>
  await api.get<PaginatedResponse<MollyWhitelabelCustomer>>(
    `/whitelabel/document/molly`,
    {
      params,
    }
  );

export const doGetMollyWhitelabelKYCDocuments =
  (params: MollyWhitelabelKYCDocumentsParams) => async (dispatch) => {
    dispatch({ type: 'molly_whitelabel/kyc/documents/started' });

    try {
      const response = await requestMollyWhitelabelKYCDocuments(params);

      dispatch({
        type: 'molly_whitelabel/kyc/documents/finished',
        documents: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'molly_whitelabel/kyc/documents/finished' },
          e,
          `Failed to molly whitelabel kyc documents.`
        )
      );
    }
  };
// #endregion

// #region MollyWhitelabelCustomerLiveInfo
export const requestMollyWhitelabelCustomerLiveInfo = async (
  username: string
) =>
  await api.get<MollyWhitelabelCustomer>(`/whitelabel/live/molly/${username}/`);

export const doGetMollyWhitelabelCustomerLiveInfo =
  (username: string) => async (dispatch) => {
    dispatch({ type: 'molly_whitelabel/customer/live_info/started' });

    try {
      const response = await requestMollyWhitelabelCustomerLiveInfo(username);

      dispatch({
        type: 'molly_whitelabel/customer/live_info/finished',
        liveInfo: response.data,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'molly_whitelabel/customer/live_info/finished' },
          e,
          `Failed to molly whitelabel customer "${username}" live info.`
        )
      );
    }
  };
// #endregion

// #region MollyWhitelabelCustomerPL
export const requestMollyWhitelabelCustomerPL = async (username: string) =>
  await api.get<MollyWhitelabelCustomer>(`/whitelabel/pl/molly/${username}/`);

export const doGetMollyWhitelabelCustomerPL =
  (username: string) => async (dispatch) => {
    dispatch({ type: 'molly_whitelabel/customer/pl/started' });

    try {
      const response = await requestMollyWhitelabelCustomerPL(username);

      dispatch({
        type: 'molly_whitelabel/customer/pl/finished',
        pl: response.data,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'molly_whitelabel/customer/pl/finished' },
          e,
          `Failed to molly whitelabel customer "${username}" pnl.`
        )
      );
    }
  };
// #endregion

// #region MollyWhitelabelCustomerNetTransfers
export const requestMollyWhitelabelCustomerNetTransfers = async (
  username: string
) =>
  await api.get<MollyWhitelabelCustomer>(
    `/whitelabel/net_dw/molly/${username}/`
  );

export const doGetMollyWhitelabelCustomerNetTransfers =
  (username: string) => async (dispatch) => {
    dispatch({ type: 'molly_whitelabel/customer/net_transfers/started' });

    try {
      const response = await requestMollyWhitelabelCustomerNetTransfers(
        username
      );

      dispatch({
        type: 'molly_whitelabel/customer/net_transfers/finished',
        netTransfers: response.data,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'molly_whitelabel/customer/net_transfers/finished' },
          e,
          `Failed to molly whitelabel customer "${username}" net transfers.`
        )
      );
    }
  };
// #endregion
